import React from "react";
import * as CONFIG from "../redux_store/configState";
import { createConditionFunction } from "../redux_store/warningHelper";
import { DegreesToRadians } from "../components/Common";

const PURLIN_OPTION_WARNING =
    "⚠️ Purlin options different in Areas (Purlins Inset | Purlins On Top). Proceed with caution.";

const MULTI_PURLIN_INSULATED_WARNING =
    "⚠️ Insulated Panels cannot have multiple Purlins in a single Area. Roofsheet Span too Large, change size or roofsheet.";

const INSULATED_EXCEEDING_TRANSPORT_WARNING =
    "⚠️ This design requires roof sheets greater than the standard transport length and will require the delivery price to be determined. Please contact you local Branch or Account manager for further information.";

const SINGLE_PURLIN_INSULATED_WARNING =
    "⚠️ Single Insulated Panels cannot have Purlins in a single Area. Roofsheet Span too Large, change size or roofsheet.";

const PREPROD_DESIGNED_JOB = "⚠️ Job was created in preprod. Please continue using preprod to process the job.";

const NIGHT_SKY_COLOUR_WARNING =
    "⚠️ DUE TO TEMPORARY SUPPLY ISSUES WITH NIGHT SKY COIL, THIS COLOUR OPTION IS UNAVAILABLE, PLEASE CHANGE THE SELECTION PRIOR TO ORDER. FOR FURTHER INFORMATION PLEASE CONTACT THE FIELDERS SA OFFICE.";

const OLDER_ATTACH_METHOD_WARNING =
    "⚠️ This job is using an older attachment definition method. Consider upgrading in the 'Attachment/House' menu.";

const BONDOR_MASONRY_MIN_BRICKWORK =
    "⚠️ A minimum of 1500mm of brickwork above the attachment point is required for Bondor Products.";

const hasDifferentRidgeBeamValues = (areaSpecifications) => {
    if (!areaSpecifications || areaSpecifications.length === 0) {
        return false; // or return an appropriate default value based on your requirements
    }

    const ridgeBeamValues = new Set();

    for (const areaSpec of areaSpecifications) {
        const hasRidgeBeam = areaSpec.BEAMS_AND_POSTS?.HasRidgeBeam;
        if (hasRidgeBeam !== undefined) {
            ridgeBeamValues.add(hasRidgeBeam);
        }
    }

    return ridgeBeamValues.size > 1;
};

const handleWarning = (props, warningMessages, setWarningMessages, condition, warning) => {
    if (condition(props)) {
        if (!warningMessages.includes(warning)) {
            setWarningMessages([...warningMessages, warning]);
        }
    } else {
        if (warningMessages.includes(warning)) {
            setWarningMessages(warningMessages.filter((message) => message !== warning));
        }
    }
};

const purlinWarningCondition = (props) => props?.job?.id && hasDifferentRidgeBeamValues(props?.job?.AreaSpecifications);

const insulatedPurlinWarningCondition = (props) => {
    return (props?.job?.AreaSpecifications || []).some((thisArea) => {
        const isFlat = thisArea?.SHAPE?.DesignType === CONFIG.DESIGN_TYPES.FLAT;
        const EngineeringResults = props?.job?.QuoteParameters?.ENGINEERING?.EngineeringResults;

        return (
            isFlat &&
            thisArea?.ROOF_SHEETING?.RoofSheetData?.InsulatedOverspan &&
            thisArea?.ROOF_SHEETING?.RoofSheetData?.PressureTableMaxSpanSingle &&
            EngineeringResults
        );
    });
};

const insulatedTransportWarningCondition = (props) => {
    if (props?.job?.DesignSummary === "Deck") return false;
    return (props?.job?.AreaSpecifications || []).some((thisArea) => {
        const roofsheet_Length = thisArea?.META?.RoofSheetSpan;
        const transport_length = props?.job?.DesignParameters?.SplicingLength;

        return roofsheet_Length > transport_length;
    });
};

const singleIsulatedPurlinWarningCondition = (props) => {
    return (props?.job?.AreaSpecifications || []).some((thisArea) => {
        const roofsheet_data = thisArea?.ROOF_SHEETING;
        if (roofsheet_data?.RoofSheetData?.single_span_only && window.brand === "Lysaght") {
            //secondary check for potential roof sheet overspan on single span sheets
            let width = thisArea?.META?.RoofSheetSpan;
            if (thisArea.SHAPE.SheetDirection === "Left" || thisArea.SHAPE.SheetDirection === "Right")
                width = width - thisArea.SHAPE.OH_Left - thisArea.SHAPE.OH_Right;
            else width = width - thisArea.SHAPE.OH_Front - thisArea.SHAPE.OH_Back;
            width = width / Math.cos(DegreesToRadians(thisArea.SHAPE.RoofPitch));
            if (width && width > 1000) {
                let max_span = roofsheet_data?.RoofSheetData?.max_internal_span;
                return max_span && width - 65 > max_span;
            }
        }

        return false;
    });
};

const nightSkyWarningConidtion = (props) => {
    return (props?.job?.AreaSpecifications || []).some((thisArea) => {
        const brand = props?.job?.Distributor?.Brand;

        return (
            brand === "Fielders" &&
            (thisArea?.BEAMS_AND_POSTS?.BeamCol1 === "Night Sky" ||
                thisArea?.BEAMS_AND_POSTS?.BeamCol2 === "Night Sky" ||
                thisArea?.BEAMS_AND_POSTS?.BeamCol3 === "Night Sky" ||
                thisArea?.BEAMS_AND_POSTS?.BeamCol4 === "Night Sky") &&
            props?.job?.Distributor?.Address.State === "SA"
        );
    });
};

const attachmentMethodWarningCondition = (props) => props?.job && !props?.job?.DesignParameters?.USE_ATTACHMENTS_2;

export const brickworkBondorHeightCondition = (props) => {
    return (
        props?.job &&
        props.job.AreaSpecifications?.some((area) => {
            const roofSheeting = area?.ROOF_SHEETING;
            const attachments = area?.ATTACHMENTS?.AttachmentModifications;

            return (
                roofSheeting?.RoofGroupType?.toLowerCase().includes("bondor") &&
                attachments?.some &&
                attachments?.some((attachment) => attachment?.AttachmentVariation?.toLowerCase().includes("masonry"))
            );
        })
    );
};

export const brickworkBondorHeightConditionIndex = (props) => {
    const areaIndex = props?.job?.AreaSpecifications?.findIndex((area) => {
        const roofSheeting = area?.ROOF_SHEETING;
        const attachments = area?.ATTACHMENTS?.AttachmentModifications;

        return (
            roofSheeting?.RoofGroupType?.toLowerCase().includes("bondor") &&
            attachments?.some((attachment) => attachment?.AttachmentVariation?.toLowerCase().includes("masonry"))
        );
    });

    return areaIndex;
};

export const preprodCondition = (props) => {
    return props?.job?.preprod && !window.location.href.includes("preprod");
};

// TODO: Move conditions to config for admin handling. Eventually moving all warnings and warning messages to admin control
const DesignerDrawingWarnings = ({ props, warningMessages, setWarningMessages, config }) => {
    React.useEffect(() => {
        let conditionsToCheck = [
            // { condition: purlinWarningCondition, warningType: PURLIN_OPTION_WARNING },
            { condition: insulatedTransportWarningCondition, warningType: INSULATED_EXCEEDING_TRANSPORT_WARNING },
            { condition: insulatedPurlinWarningCondition, warningType: MULTI_PURLIN_INSULATED_WARNING },
            { condition: singleIsulatedPurlinWarningCondition, warningType: SINGLE_PURLIN_INSULATED_WARNING },
            { condition: nightSkyWarningConidtion, warningType: NIGHT_SKY_COLOUR_WARNING },
            { condition: attachmentMethodWarningCondition, warningType: OLDER_ATTACH_METHOD_WARNING },
            { condition: preprodCondition, warningType: PREPROD_DESIGNED_JOB },

            // { condition: brickworkBondorHeightCondition, warningType: BONDOR_MASONRY_MIN_BRICKWORK },
        ];

        const allWarnings = config?.all_warnings;
        if (allWarnings?.jobWarning) {
            const warning = allWarnings?.jobWarning;

            for (const type in warning) {
                if (warning.hasOwnProperty(type) && type !== "info") {
                    const conditionStrings = warning[type]?.conditions;
                    const condition = createConditionFunction(conditionStrings);
                    const message = warning[type]?.message;
                    conditionsToCheck.push({ condition, warningType: message });
                }
            }
        }

        conditionsToCheck.forEach(({ condition, warningType }) => {
            handleWarning(props, warningMessages, setWarningMessages, condition, warningType);
        });
    }, [props.job]);
};

export default DesignerDrawingWarnings;
